import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { withCapture, _ } from 'utils'
import FullPageLoading from 'components/FullPageLoading'
import NoData from 'components/NoData'
import { IMAGE_ONLY } from 'features/forms/components/FileUpload'
import { useDropzone } from 'react-dropzone'
import ImageItem from './ImageItem'
import UploadPlaceholder from '../../assets/upload-photo.png'

const S = {
    ImageGridContainer: styled.div`
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
        grid-template-rows: 1fr;
        grid-gap: 20px;
    `,
    ImageContainer: styled.div`
        display: flex;
        justify-content: center;
        position: relative;
        
        &:before {
          content: "";
          display: block;
          height: 0;
          width: 0;
          padding-bottom: calc(3/4 * 100%);
        }
    `,
    DragNDropZone: styled.div`
        background: #EEF5F4;
        width: 100%;
        height: 100%;
        display: inline-flex;
        justify-content: center;
        align-items: center;
        border-radius: 6px;
        border: 1px dashed rgba(120, 144, 156, 0.25);
    `,
    UploadPlaceholder: styled.img.attrs({ src: UploadPlaceholder })`
        max-width: 150px;
        max-height: 120px;
        object-fit: contain;
    `,
    Image: styled.img`
        width: 100%;
        height: 100%;
        object-fit: cover;
        border-radius: 6px;
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
    `,
    ImageHoverContainer: styled.div`
        background: rgba(0,0,0,.25);
        width: 100%;
        height: 100%;
        object-fit: cover;
        border-radius: 6px;
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        opacity: 0;
        transition: all 0.2s;
        z-index: 2;
        
        :hover {
            opacity: 1;
        }
    `,
    PreviewImageButton: styled.div`
        position: absolute;
        right: 28px;
        top: 0;
        cursor: pointer;
    `,
    DeleteImageButton: styled.div`
        position: absolute;
        right: 5px;
        top: 0;
        cursor: pointer;
    `
}

type Props<T> = {
    images: T[]
    canUpload: boolean
    canDelete: boolean
    onSelectImage?: (image: T) => void
    onDeleteImage?: (image: T) => Promise<void>
    onUploadImage: (imageFile: File) => Promise<T>
    getImageUrl: (image: T) => string
}

function BaseImageLibrary<T>({
    images: propImages, canUpload, canDelete, onSelectImage, onDeleteImage, onUploadImage, getImageUrl
} : Props<T>) {
    const [isLoading, setIsLoading] = useState(false)
    const [images, setImages] = useState<T[]>(propImages || [])

    useEffect(() => {
        setImages(propImages)
    }, [propImages])

    const handleUploadFiles = withCapture(async (acceptedFiles) => {
        setIsLoading(true)
        try {
            for (let i = 0; i < acceptedFiles.length; i++) {
                // eslint-disable-next-line no-await-in-loop
                const uploadedImage = await onUploadImage(acceptedFiles[i])
                if (uploadedImage) {
                    setImages((_images) => [..._images, uploadedImage])
                }
            }
        } finally {
            setIsLoading(false)
        }
    })

    const handleDeleteImage = withCapture(async (image) => {
        setIsLoading(true)
        try {
            await onDeleteImage(image)
            setImages((_images) => _images.filter((e) => e !== image))
        } finally {
            setIsLoading(false)
        }
    })

    const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop: handleUploadFiles })

    return (
        <div>
            <S.ImageGridContainer>
                {canUpload && (
                    <S.ImageContainer>
                        <S.DragNDropZone {...getRootProps()}>
                            <input {...getInputProps()} accept={IMAGE_ONLY} />
                            {
                                !isDragActive && (
                                    <S.UploadPlaceholder />
                                )
                            }
                        </S.DragNDropZone>
                    </S.ImageContainer>
                )}
                {images.map((image, index) => (
                    <S.ImageContainer key={index}>
                        <ImageItem
                            image={image}
                            onSelected={onSelectImage}
                            onDelete={handleDeleteImage}
                            canDelete={canDelete}
                            getImageUrl={getImageUrl}
                        />
                    </S.ImageContainer>
                ))}
            </S.ImageGridContainer>
            {!canUpload && !images.length && (
                <NoData text={_('image.no_image')} />
            )}
            {isLoading && <FullPageLoading isAbsolute fullWidth />}
        </div>
    )
}

export default BaseImageLibrary
